import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { profileThunks } from "../../../actions-thunks/profile-thunks";

import Checkbox from "../../utilities/element/Checkbox";
import Input from "../../utilities/element/Input";
import { utilityFns } from "../../utilities/utility-functions";
import AboutYouStep from "../AboutYou/AboutYou";
import Modal from "../../utilities/element/Modal";
import { testMandatoriesFns } from "../../utilities/test-mandatories";
import ContinueOnDevice from "../../utilities/element/ContinueOnDevice";

import "./agreement.scss";
import { getBundleName } from "../BusinessDeal/helpers";
const SHARES = "shares";
const PAYOUT = "payout";

const AgreementStep = ({ localState, setLocalState, stepsData }) => {
  const dispatch = useDispatch();
  const { volumePayments, tradingPlatform, profile, agreementTemplate, rates } =
    localState;

  const { existingRequests } = useSelector((state) => state.profile);

  const { reportProfileTC } = profileThunks;

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");

  useEffect(() => {
    const countryrates = rates.countryrates.filter(
      (cr) => cr.accountTypeRef === profile.accountType
    );

    const vp = (
      countryrates.length > 0
        ? countryrates
        : rates.countryrates.filter((x) => x.country === "*")
    ).sort((a, b) => (a.min < b.min ? -1 : a.min > b.min ? 1 : 0));

    const tradingPlatform = utilityFns.findCurrentTradingPlatform(
      profile,
      rates
    );

    // Get initial volumePayments
    let volumePayments = vp.length > 0 ? vp : [];
    // Check if tradingPlatform has payout or shares data
    if (tradingPlatform) {
      // If tradingPlatform has shares data, update the corresponding volumePayments entry
      if (tradingPlatform.shares && tradingPlatform.shares.length > 0) {
        const sharesIndex = volumePayments.findIndex(
          (vp) => vp.classifier === SHARES
        );
        if (sharesIndex !== -1) {
          // Update existing shares entry
          volumePayments[sharesIndex] = {
            ...volumePayments[sharesIndex],
            tiers: tradingPlatform.shares,
          };
        } else if (volumePayments.length > 0) {
          // Add new shares entry based on existing entry structure
          volumePayments.push({
            ...volumePayments[0],
            classifier: SHARES,
            tiers: tradingPlatform.shares,
          });
        }
      }

      // If tradingPlatform has payout data, update the corresponding volumePayments entry
      if (tradingPlatform.payout && tradingPlatform.payout.length > 0) {
        const payoutIndex = volumePayments.findIndex(
          (vp) => vp.classifier === PAYOUT
        );
        if (payoutIndex !== -1) {
          // Update existing payout entry
          volumePayments[payoutIndex] = {
            ...volumePayments[payoutIndex],
            tiers: tradingPlatform.payout,
          };
        } else if (volumePayments.length > 0) {
          // Add new payout entry based on existing entry structure
          volumePayments.push({
            ...volumePayments[0],
            classifier: PAYOUT,
            tiers: tradingPlatform.payout,
          });
        }
      }
    }

    const updateMarketDataEntitlements = (profile, localState2) => {
      const fromMarketObjects = (marketObjects) => {
        return marketObjects.map((obj) => `${obj.group}-${obj.market}`);
      };
      if (profile.agreement.marketData) {
        const unitedMarkets = fromMarketObjects(profile.agreement.marketData);
        localState2.profile.markets = [...unitedMarkets];
        localState2.profile.marketdataBundle =
          getCurrentMarketBundle(localState2);
        return { ...localState2 };
      }
    };

    let localState2 = { ...localState };
    localState2 = updateMarketDataEntitlements(profile, localState2);

    setLocalState({
      ...localState2,
      countryrates,
      volumePayments,
      tradingPlatform,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentMarketBundle = (localState2) => {
    const mdp = rates?.marketdataproviders?.filter((p) => {
      return p.tradingPlatformRefs.includes(
        utilityFns.findCurrentTradingPlatform(profile, rates)?.reference
      );
    });
    return getBundleName({
      marketdataProviders: mdp,
      currentAccount: localState2.profile,
      tradingPlatforms: rates?.tradingplatforms,
      marketBundles: rates.bundles,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setLocalState({
        ...localState,
        profile: { ...profile, [name]: checked },
      });
    } else {
      setLocalState({ ...localState, profile: { ...profile, [name]: value } });
    }
  };
  const infoConfirmMessage = `I acknowledge that the foregoing and all other information provided during the
  account application process is true and correct and agree to notify Zimtra by
  email or otherwise of any material changes therein. I authorize Zimtra to confirm
  the accuracy of the information provided by me as Zimtra deems necessary.`;
  const agreementConfirmMessage =
    "I have read and acknowledged terms of the Professional Trader Service Agreement";

  const money = (v, frac = 0) => {
    return parseFloat(v).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: frac,
    });
  };

  const formatValue = (v, formats) => {
    if (formats && formats.length > 0) {
      switch (formats[0]) {
        case "money":
          const digits = formats[1] || 0;
          return money(v, digits);
        default:
      }
    }
    return v;
  };
  const generateTierValue = (t, spec) => {
    var value;
    switch (spec[0]) {
      case "commission":
        value = t.commission;
        break;
      case "payout":
        value = `${t.payout * 100.0}%`;
        break;
      case "overnight":
        value = `${t.interest?.fed ? "FED+" : ""}${t.interest?.percent.toFixed(
          2
        )}%`;
        break;
      case "software":
        const tp = utilityFns.findCurrentTradingPlatform(profile, rates);
        const tpPrice = utilityFns.calculatePlatformPriceWithConditions(
          tp,
          profile
        );
        let softwareprice = "platform?";
        if (typeof tpPrice !== "undefined") {
          const calculatedPrice = t.software.base
            ? tpPrice + t.software.change
            : t.software.change;

          const adjustedPrice = Math.max(calculatedPrice, 0);
          softwareprice = `$${adjustedPrice}`;
        }
        value = softwareprice;
        break;
      default:
        value = t[spec[0]];
    }

    return {
      className: spec[1] || spec[0],
      value: formatValue(value, spec.slice(2)),
    };
  };
  const moneyshort = (v, frac = 1) => {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: frac,
      notation: "compact",
      compactDisplay: "short",
    }).format(v);
  };
  const templateProperties = (text) => {
    const generateTiers = (spec) => {
      const arr = volumePayments.map((t, index) => {
        const tv = generateTierValue(t, spec);
        return (
          <div className="tier-level-wrapper" key={index}>
            <span className="min">{moneyshort(t.min, 2)}</span>
            <span className="max">
              {t.max > 0 ? moneyshort(t.max, 2) : " ∞ "}
            </span>
            <span className={tv.className}>{tv.value}</span>
          </div>
        );
      });

      return (
        <div className="tier-levels">
          <div className="tier-level-wrapper header">
            <span className="min">volume low</span>
            <span className="max">volume high</span>
            <span className="value">{spec[0]}</span>
          </div>
          {arr}
        </div>
      );
    };

    // parse text <.*>
    // get template text from it
    // replace with properties
    // to it globaly on text
    const rp = (p) => {
      if (!p.match(/^<.*>$/)) {
        return p;
      }

      const key = p
        .slice(1, -1)
        .split(/,/)
        .map((v) => v.trim());
      if (key.length > 0 && key[0].length > 0) {
        if (typeof profile[key[0]] !== "undefined") {
          var v = profile[key[0]];
          if (key.length > 1) {
            v = (
              <div className="schedule-amount">
                {formatValue(v, key.slice(1))}
              </div>
            );
          }
          return v;
        } else {
          if (key[0] === "tiers") {
            return generateTiers(key.slice(1));
          }
        }
      } else {
        return p;
      }
    };

    const ss = text.split(/(<[\w\s\t\d.,]+>)/);
    return ss.map(rp);
  };

  const renderAgreementText = (text) => {
    if (!text) {
      return null;
    }

    return text.split("\n").map((e, index) => {
      var className = "simple";

      // Number. - as header
      if (e.match(/^\s?\d+\./)) {
        className = "header";
      }

      // CAPS - as highlight
      if (e.match(/^[A-Z ,.]+$/)) {
        className = "highlight";
      }

      const calculated = templateProperties(e); //simple-tier
      // substitute properties
      return (
        <div className={className} key={index}>
          {calculated}
        </div>
      );
    });
  };

  const updateNegativeValues = (obj) => {
    const numericValue = parseFloat(obj.value.replace(/[^0-9.-]/g, ""));

    // If the number is less than 0, set it to 0 but keep the currency or symbol
    if (numericValue < 0) {
      obj.value = obj.value.replace(numericValue, 0);
    }

    return obj;
  };

  const marketdataproviders = useMemo(() => {
    const tradingPlatformReference =
      utilityFns.findCurrentTradingPlatform(profile, rates)?.reference || "";
    const result = rates?.marketdataproviders?.filter((p) => {
      return p.tradingPlatformRefs.includes(tradingPlatformReference);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, tradingPlatform, localState, profile.accountType]);

  const renderSchedules = () => {
    const renderLeftTablePart = (leftName, forPayout) => {
      const leftValues = () => {
        if (volumePayments.length > 0) {
          const filterdVP = volumePayments.filter(
            (vp) => vp.accountTypeRef === profile.accountType
          );
          const tiers = filterdVP.find(
            (i) => i.classifier === (forPayout ? PAYOUT : SHARES)
          ).tiers;
          const formatMaxValue = (tier) => {
            let returnValue = "∞ ";

            if (tier.minSharePrice) {
              returnValue += `(≥ $${tier.minSharePrice})`;
            } else if (tier.maxSharePrice) {
              returnValue += `(< $${tier.maxSharePrice})`;
            }

            return <span className="grey-color">{returnValue}</span>;
          };

          return (
            <div className="monthly-trading-volumes">
              {tiers.map((t) => {
                return (
                  <span>
                    {utilityFns.moneyshort(t.min, 2, true)} -{" "}
                    {t.max > 0
                      ? utilityFns.moneyshort(t.max, 2, true)
                      : formatMaxValue(t)}
                  </span>
                );
              })}
            </div>
          );
        }
      };
      return (
        <div className="flex-column">
          <span className="font-bold">{leftName}</span>
          {leftValues()}
        </div>
      );
    };
    const renderRightTablePart = (rightName, spec, forPayout) => {
      const rightValues = () => {
        if (volumePayments.length > 0) {
          const tiers = volumePayments.find(
            (i) => i.classifier === (forPayout ? PAYOUT : SHARES)
          ).tiers;
          return tiers.map((t, index) => {
            const tv = generateTierValue(t, spec);
            if (tv.className === "commission") {
              const value =
                tv.value % 10 === 0 ? `${tv.value / 10000}0` : tv.value / 10000;
              return (
                <span key={index} className={tv.className}>
                  {value}
                </span>
              );
            }
            if (tv.className === "overnight") {
              const value = tv.value.replace("%", "");
              return (
                <span key={index} className={tv.className}>
                  {value}
                </span>
              );
            }
            const updatedData = updateNegativeValues(tv);
            return (
              <span key={index} className={tv.className}>
                {updatedData.value}
              </span>
            );
          });
        }
      };
      return (
        <div className="volume-rates">
          <span className="font-bold">{rightName}</span>
          <div className="volume-rates-wrapper">{rightValues()}</div>
        </div>
      );
    };
    const renderClearingCommision = () => {
      return (
        <div className="clearing-commision">
          <p>
            Clearing commission determined by a monthly traded volume according
            to the following schedule:{" "}
          </p>
          <hr />
          <div className="clearing-commision-wrapper">
            {renderLeftTablePart("Monthly Trading Volumes (shares)")}
            {renderRightTablePart("Commission Rates ($)", ["commission"])}
          </div>
        </div>
      );
    };
    const renderOvernightFee = () => {
      return (
        <div className="overnight-fee">
          <p>
            Overnight fee determined by a monthly traded volume according to the
            following schedule:
          </p>
          <hr />
          <div className="overnight-fee-wrapper">
            {renderLeftTablePart("Monthly Trading Volumes (shares)")}
            {renderRightTablePart("Overnight Rates (%)", ["overnight"])}
          </div>
        </div>
      );
    };
    const renderBuyingPowerText = () => {
      if (profile.accountType === "zimtraelite") {
        return (
          <p className="buying-power-text">
            <strong>Buying Power:</strong> is limited by a{" "}
            <em>maximum position exposure</em> to manage risk.
            <br />
            <br />
            Elite Accounts get 10:1 buying power with a 25% exposure limit -
            allowing 2.5x on a single trade, or full buying power across four.
            <br />
            <br />
            <strong>Short Selling:</strong> is limited to 2:1 buying power on
            stocks over $1 — and only accounts with $15,000+ can short stocks
            under $1.
            <br />
            <br />
            <strong>Overnight Exposure:</strong> only accounts over $10,000 can
            hold positions overnight using up to 2:1 buying power.
          </p>
        );
      }
      if (profile.accountType === "zimtrapro") {
        return (
          <p className="buying-power-text">
            <strong>Buying Power:</strong> is limited by a{" "}
            <em>maximum position exposure</em> to manage risk.
            <br />
            <br />
            Pro Accounts get 4:1 buying power with a 25% exposure limit —
            allowing 2.5x on a single trade, or full buying power across four.
            <br />
            <br />
            <strong>Short Selling:</strong> is limited to 2:1 buying power on
            stocks over $1 — and only accounts with $15,000+ can short stocks
            under $1.
            <br />
            <br />
            <strong>Overnight Exposure:</strong> only accounts over $10,000 can
            hold positions overnight using up to 2:1 buying power.
          </p>
        );
      }
      return null;
    };

    const renderScheduleA = () => {
      return (
        <div className="schedule-a">
          <h4>SCHEDULE A</h4>
          <div className="schedule-a-wrapper">
            <div className="schedule-a-item">
              <span>Performance Bond Amount:</span>
              <span className="font-bold">
                {formatValue(profile.agreement.rules.performanceBond, [
                  "money",
                  "2",
                ])}
              </span>
            </div>
            <div className="schedule-a-item">
              <span>Initial Buying Power:</span>
              <span className="font-bold">
                {formatValue(profile.agreement.rules.initialBuyingPower, [
                  "money",
                  "2",
                ])}
              </span>
            </div>
            <div className="schedule-a-item">
              <span>Approved Overnight Buying Power:</span>
              <span className="font-bold">
                {formatValue(
                  profile.agreement.rules.approvedOvernightBuyingPower || 0,
                  ["money", "2"]
                )}
              </span>
            </div>
            <div className="schedule-a-item">
              <span>Maximum Daily Loss Limit:</span>
              <span className="font-bold">
                {formatValue(profile.agreement.rules.maximumDailyLoss, [
                  "money",
                  "2",
                ])}
              </span>
            </div>
            <div className="schedule-a-item">
              <span>Maximum Trader Balance Draw Down:</span>
              <span className="font-bold">50%</span>
            </div>
            <div className="schedule-a-item">
              <span>Profit Payout:</span>
              <span className="font-bold">
                {(() => {
                  const tiers = volumePayments.find(
                    (i) => i.classifier === PAYOUT
                  )?.tiers;
                  if (tiers && tiers.length > 0) {
                    const tv = generateTierValue(tiers[0], ["payout"]);
                    return tv.value;
                  }
                  return "100%";
                })()}
              </span>
            </div>
            {profile.accountType === "zimtraexpert" && (
              <>
                <div className="schedule-a-item">
                  <span>Commission:</span>
                  <span className="font-bold">Free</span>
                </div>
                <div className="schedule-a-item">
                  <span>Overnight Fee:</span>
                  <span className="font-bold">
                    {(() => {
                      const tiers = volumePayments.find(
                        (i) => i.classifier === SHARES
                      )?.tiers;
                      if (tiers && tiers.length > 0) {
                        const tv = generateTierValue(tiers[0], ["overnight"]);
                        return tv.value === "undefined%"
                          ? "FED+4.50%"
                          : tv.value;
                      }
                      return "FED+4.50%";
                    })()}
                  </span>
                </div>
              </>
            )}
          </div>
          {renderBuyingPowerText()}
          {profile.accountType !== "zimtraexpert" && (
            <>
              {renderClearingCommision()}
              {renderOvernightFee()}
            </>
          )}
        </div>
      );
    };

    const renderSoftwareFee = () => {
      // Use profile.agreement.marketData if available (filtered, excludes included feeds)
      // Otherwise fall back to profile.markets
      const marketsToDisplay = profile.agreement?.marketData 
        ? profile.agreement.marketData.map((m) => `${m.group}-${m.market}`)
        : profile.markets || [];
      
      // eslint-disable-next-line array-callback-return
      const MDs = marketdataproviders
        .map((md) => {
          const marketId = utilityFns.getMarketId(md);
          const v = marketsToDisplay.includes(marketId);
          if (v) return md;
          return null;
        })
        .filter((n) => n);

      const totalMDPrice = MDs.reduce((acc, item) => {
        return acc + item.price;
      }, 0);

      const renderMDSubscriptions = () => {
        return (
          <div className="market-data-subscription-sources">
            <p>Market data subscription sources</p>
            <hr />
            <div className="market-data-subscription-sources-wrapper-inner">
              <ul>
                {MDs.map((m, index) => {
                  return (
                    <li className="market-subscription" key={index}>
                      <span className="name">{m.name}</span>
                      <span className="price">
                        {utilityFns.money(m.price, 2)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <hr />
              <div className="market-data-price-summary">
                <span>Market data price summary</span>
                <span className="font-bold">
                  ${Math.round(totalMDPrice * 100) / 100}
                </span>
              </div>
            </div>
          </div>
        );
      };

      const getTiers = (forPayout) => {
        if (volumePayments.length > 0) {
          const filterdVP = volumePayments.filter(
            (vp) => vp.accountTypeRef === profile.accountType
          );
          const tiers = filterdVP.find(
            (i) => i.classifier === (forPayout ? PAYOUT : SHARES)
          ).tiers;
          return tiers;
        }
      };

      const getMonthlySoftwareFeePrice = () => {
        let foundPrice = 0;
        getTiers(false)?.forEach((tier) => {
          const tv = generateTierValue(tier, ["software"]);
          if (tv && tv.value && Number(tv.value.substring(1)) > 0) {
            foundPrice = tv.value;
          }
        });
        return foundPrice;
      };

      return (
        <div className="software-fee">
          <div className="software-fee-wrapper-inner">
            <div className="software-fee-wrapper">
              <div className="flex-between trading-platform">
                <span>Trader's platform</span>
                <span className="font-bold">
                  {utilityFns.findCurrentTradingPlatform(profile, rates)?.name}
                </span>
              </div>
              <hr />
              {profile.accountType === "zimtraexpert" &&
              profile?.software === "Sterling" ? (
                <div className="overnight-fee-wrapper">
                  {renderLeftTablePart("Monthly Trading Volumes (shares)")}
                  {renderRightTablePart("Monthly Cost", ["software"])}
                </div>
              ) : (
                <div className="flex-between software-fee-price">
                  <span>Monthly Software Fee:</span>
                  <span className="font-bold">
                    {getMonthlySoftwareFeePrice()}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="market-data-subscription-sources-wrapper">
            {renderMDSubscriptions()}
          </div>
        </div>
      );
    };

    return (
      <div className="agreement-schedules">
        {renderScheduleA()}
        <p className="ecn-fees-text">
          Overnight Exposure: Only accounts over $10,000 can hold positions
          overnight using up to 2:1 buying power.
        </p>
        <p className="ecn-fees-text">
          ECN fees and rebates, as well as other exchange charges will be billed
          at prevailing market rates as advertised by the exchanges.
        </p>
        {renderSoftwareFee()}
        <p className="market-data-price-summary-text">
          Exchange market data is billed at prevailing market rates.
        </p>
      </div>
    );
  };

  const renderTodayDate = () => {
    const year = new Date().getFullYear();
    const month = new Date().toLocaleString("default", { month: "long" });
    const day = new Date().getDate();

    const formatAMPM = (date) => {
      var hours = date.getHours();
      var minutes = date.getMinutes();
      var ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime = hours + ":" + minutes + " " + ampm;
      return strTime;
    };
    return (
      <div className="onb-date">{`${year} ${month} ${day} ${formatAMPM(
        new Date()
      )}`}</div>
    );
  };

  const hasExistingReportClosed = useMemo(() => {
    const foundReport = existingRequests?.find(
      (request) => request.ext.category === "System Generated"
    );
    return foundReport?.status === "COMPLETED";
  }, [existingRequests]);

  // Add this check for any open inquiries
  const hasOpenInquiries = useMemo(() => {
    // Return null if existingRequests is undefined or null (still loading)
    if (!existingRequests) {
      return null;
    }

    return existingRequests.some(
      (request) =>
        request.type === "INQUIRY" &&
        (request.status === "OPEN" ||
          request.status === "INPROGRESS" ||
          request.status === "ONHOLD")
    );
  }, [existingRequests]);

  // Validate profile data
  const isAboutYouValid = useMemo(() => {
    if (localState.profile) {
      return testMandatoriesFns.aboutYou(localState.profile);
    }
    return false;
  }, [localState.profile]);

  // Automatically report if validation fails and no existing report
  useEffect(() => {
    if (
      localState.profile &&
      !isAboutYouValid &&
      // Only proceed if we've loaded the requests data (hasOpenInquiries is not null)
      // and there are no open inquiries
      hasOpenInquiries !== null &&
      !hasOpenInquiries &&
      !hasReported
    ) {
      // Get the missing fields only once when reporting
      const missingFields = testMandatoriesFns.findMissingFields(
        localState.profile
      );
      const descriptionMessage = `Need to solve issues with application data: ${missingFields.join(
        ", "
      )}`;

      dispatch(reportProfileTC(localState.profile, descriptionMessage));
      setHasReported(true);
    }
  }, [
    localState.profile,
    isAboutYouValid,
    hasOpenInquiries,
    reportProfileTC,
    hasReported,
    dispatch,
  ]);

  const handleReport = () => {
    dispatch(reportProfileTC(localState.profile, issueDescription));
    setIsReportModalOpen(false);
    setIsConfirmationModalOpen(true);
    setHasReported(true);
    setIssueDescription("");
  };

  const getReviewText = () => {
    // Show reported text if we have an existing report or just reported
    if (hasOpenInquiries || hasReported) {
      return "There are some inconsistencies in your data. We will process your request as soon as possible and contact you via email.";
    }

    // Show "let us know" option if validation passes
    if (isAboutYouValid) {
      return (
        <span>
          {hasExistingReportClosed && (
            <span>Your data has been corrected. </span>
          )}
          In case of any inaccuracy,{" "}
          <span
            className="link-text"
            onClick={(e) => {
              e.preventDefault();
              setIsReportModalOpen(true);
            }}
          >
            please let us know
          </span>
        </span>
      );
    }

    // Default text for invalid data
    return "There are some inconsistencies in your data. We will process it as soon as possible and contact you via email.";
  };

  return (
    <div className="agreement-wrapper">
      <div className="agreement-title-wrapper">
        <h4 id="reviewagreement">Review & Agreement</h4>
        <span className="agreement-step-number">
          step {stepsData.step} of {stepsData.totalSteps}
        </span>
      </div>
      <ContinueOnDevice />
      <hr />
      <div className="agreement-wrapper-inner">
        <div className="agreement-wrapper-inner-left">
          <h4>Review Application</h4>
          <span>{getReviewText()}</span>
        </div>
        <AboutYouStep
          fromAgreement={true}
          localState={localState}
          setLocalState={setLocalState}
        />
      </div>
      <Checkbox
        id="agreement-checkbox-upper"
        className="agreement1"
        name="agreement1"
        agreementstep="true"
        placeholder={infoConfirmMessage}
        value={profile.agreement1}
        onChange={handleChange}
      />
      <hr />
      <div className="agreement-checkbox-wrapper">
        <h4>Trader Service Agreement</h4>
        <div className="trading-service-agreement">
          {renderAgreementText(agreementTemplate?.documentBody)}
        </div>
        <Checkbox
          id="agreement-checkbox-lower"
          name="agreement2"
          className="agreement2"
          agreementstep="true"
          placeholder={agreementConfirmMessage}
          value={profile.agreement2}
          onChange={handleChange}
        />
      </div>
      <hr />
      {renderSchedules()}
      <hr />
      <p className="footer-text">
        The monthly pay date for Trader compensation and/or account
        reconciliation or closure will take place on the 1st of the following
        trading month (i.e. trading profits made in June are paid out no later
        than August 1).
      </p>
      <div className="signature-wrapper">
        <div className="signature-wrapper-inner">
          <Input
            id="signature-field"
            name="signature"
            label="Signature"
            placeholder="Required"
            value={profile.signature || ""}
            onChange={handleChange}
            required
          />
          <div className="signature-dated">
            <img src="/dated.svg" alt="Dated" />
            <div className="dated">Dated:{renderTodayDate()}</div>
          </div>
        </div>
      </div>
      <hr />

      <Modal
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setIssueDescription("");
        }}
      >
        <div className="modal-text">
          You're about to report an inaccuracy in your data.
        </div>
        <div className="modal-textarea">
          <label htmlFor="issue-description">
            Describe your issue (optional)
          </label>
          <textarea
            id="issue-description"
            value={issueDescription}
            maxLength={250}
            onChange={(e) => setIssueDescription(e.target.value)}
            placeholder="Please describe what information needs to be corrected..."
            rows={4}
          />
        </div>
        <div className="modal-buttons">
          <button
            onClick={() => {
              setIsReportModalOpen(false);
              setIssueDescription("");
            }}
          >
            Cancel
          </button>
          <button className="primary" onClick={handleReport}>
            Report
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
      >
        <div className="modal-text">
          Your request has been sent. We will process it as soon as possible and
          contact you via email. You can continue signing when the inaccuracies
          are corrected.
        </div>
        <div className="modal-buttons">
          <button
            className="primary"
            onClick={() => setIsConfirmationModalOpen(false)}
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AgreementStep;
